import React, { useState, useEffect, useCallback } from 'react';
import SignedImage from '../SignedImage';

const PopupWindow = ({ title, images = [], onClose, style = {}, kernelId, onCookNow, kernelData }) => {
    // Default placeholder image for kernels without images
    const placeholderImage = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=400&fit=crop&crop=center';
    
    // Use placeholder if no images provided
    const displayImages = images.length > 0 ? images : [placeholderImage];

    // Slideshow state for multiple images
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [nextImageIndex, setNextImageIndex] = useState(1);
    const [isSliding, setIsSliding] = useState(false);

    // Dragging state
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState(() => {
        // Extract position from style prop, or use defaults
        const top = parseInt(style.top) || 96;
        const left = parseInt(style.left) || 96;
        return { x: left, y: top };
    });

    // Automatic slideshow for multiple images
    useEffect(() => {
        if (displayImages.length <= 1) return;

        const slideshowInterval = setInterval(() => {
            // Start slide down animation
            setIsSliding(true);
            
            // After animation completes, update indices
            setTimeout(() => {
                setCurrentImageIndex(prevIndex => {
                    const newIndex = prevIndex === displayImages.length - 1 ? 0 : prevIndex + 1;
                    setNextImageIndex(newIndex === displayImages.length - 1 ? 0 : newIndex + 1);
                    return newIndex;
                });
                setIsSliding(false);
            }, 600);
        }, 3000); // Change image every 3 seconds

        return () => clearInterval(slideshowInterval);
    }, [displayImages.length]);

    // Keyboard navigation - ESC to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Dragging functionality
    const handleMouseDown = useCallback((e) => {
        // Only drag from title bar
        if (e.target.closest('.title-bar')) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    }, [position]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    return (
        <div 
            className="fixed z-50 bg-neutral-0 w-96 shadow-popup border border-black"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: isDragging ? 'grabbing' : 'default'
            }}
            onMouseDown={handleMouseDown}
        >
            {/* Title Bar */}
            <div className="title-bar bg-neutral-0 text-neutral-900 px-4 py-2 flex justify-between items-center cursor-grab active:cursor-grabbing select-none hairline-b">
                <span className="font-mono uppercase text-sm tracking-wider truncate">
                    {title}
                </span>
                <button 
                    onClick={onClose}
                    className="text-neutral-900 text-base flex-shrink-0 border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:text-neutral-500 transition-colors"
                >
                    ✕
                </button>
            </div>

            {/* Content Area - Images Only */}
            <div className="h-80 overflow-hidden">
                {/* Image Stack with Slide Animation */}
                <div className="relative w-full h-full">
                    {/* Bottom Image (Static) */}
                    <SignedImage 
                        src={displayImages[currentImageIndex].url || displayImages[currentImageIndex]}
                        alt={`${title} - Image ${currentImageIndex + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        category={kernelData && kernelData.category}
                    />
                    
                    {/* Top Image (Slides Down) */}
                    {displayImages.length > 1 && (
                        <SignedImage 
                            src={displayImages[nextImageIndex].url || displayImages[nextImageIndex]}
                            alt={`${title} - Image ${nextImageIndex + 1}`}
                            className={`absolute inset-0 w-full h-full object-cover ${
                                isSliding
                                  ? 'transition-transform duration-600 ease-in-out translate-y-0'
                                  : '-translate-y-full transition-none'
                            }`}
                            category={kernelData && kernelData.category}
                        />
                    )}
                    
                    {/* Placeholder label for kernels without images */}
                    {images.length === 0 && (
                        <div className="absolute bottom-2 left-2 bg-neutral-900 text-neutral-0 px-2 py-1 text-xs font-mono uppercase tracking-wider">
                            PLACEHOLDER
                        </div>
                    )}


                </div>
            </div>

            {/* Action Buttons */}
            {kernelId && (
                <div className="bg-neutral-0 px-4 py-3 hairline-t flex gap-2">
                    <button 
                        onClick={() => onCookNow && onCookNow(kernelId)}
                        className="w-full px-3 py-2 bg-neutral-900 text-neutral-0 text-xs font-mono uppercase tracking-wider hover:opacity-60 transition-opacity text-center"
                    >
                        COOK NOW
                    </button>
                </div>
            )}
        </div>
    );
};

export default PopupWindow; 
